import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';

import { fetchColleges } from 'api/colleges/colleges';
import { handleApiError } from 'utils';

/**
 * @component SelectColleges
 * @description
 * Componente que muestra un `select` personalizado para elegir un colegio desde una lista obtenida de la API.
 * Al seleccionar un colegio, ejecuta `setSharedCollegeId` con el ID seleccionado para compartirlo con el componente padre.
 *
 * @param {Function} setSharedCollegeId - Función que se ejecuta al seleccionar un colegio. Recibe el ID del colegio seleccionado.
 * 
 * @returns {JSX.Element} Select dropdown de colegios.
 */
function SelectColleges({ setSharedCollege }) {
	const token = useSelector(state => state.authentication.user?.token);

	const [colleges, setColleges] = useState([]);
	const [selectedCollege, setSelectedCollege] = useState(0);

    /**
	 * @function loadAllColleges
	 * @description
	 * Consulta la lista de colegios desde la API sin filtros,
	 * y la transforma en un formato compatible con `react-select`,
	 * estableciendo los datos en el estado local.
	 */
	const loadAllColleges = useCallback(async () => {
		try {
			const { data } = await fetchColleges({}, token); // sin filtros
			const colleges = data.map((value) => ({
				value: value.id,
				label: value.name
			}));
			setColleges(colleges);
		} catch (error) {
			handleApiError(error);
			setColleges([]);
		}
	}, [token]);


    /**
	 * useEffect que carga los colegios una vez que se tiene un token válido.
	 */
	useEffect(() => {
        if (token) loadAllColleges();
    }, [token, loadAllColleges]);

	/**
	 * @function handleCollegesChange
	 * @description
	 * Maneja el cambio de selección del componente `Select`, actualizando
	 * el estado interno y enviando el ID al componente padre.
	 *
	 * @param {Object} selectedOption - Opción seleccionada de react-select.
	 */
	const handleCollegesChange = (selectedOption) => {
		setSelectedCollege(selectedOption);
		setSharedCollege(selectedOption);
	};

	return (
		<div>
			<label className="form" htmlFor="colleges"><strong>Colegios</strong></label>
			<Select 
				id="colleges"
				menuPortalTarget={document.body}
				styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
				options={colleges}
				onChange={handleCollegesChange}
				value={selectedCollege}
				placeholder="Selecciona un colegio"
				required
			/>
		</div>
	);
}

export default SelectColleges;